import React, { useRef, useState } from 'react';
import skyWrenchLogo from '../assets/skywrench-logo.png';
import {
  isStandaloneSkyWrenchCarrier,
  requestStandaloneSkyWrenchSection,
  resolveSkyWrenchReference,
  STANDALONE_SKYWRENCH_CARRIERS,
  StandaloneSkyWrenchCarrier,
  StandaloneSkyWrenchSection,
  SkyWrenchReferenceAccess,
} from '../services/skyWrenchService';
import { SkyWrenchReferenceCards, SkyWrenchReferenceViewer } from './SkyWrenchReferences';

const MAX_DEFECT_DESCRIPTION_LENGTH = 2_000;
const PRINT_URL_REVOKE_DELAY_MS = 60_000;
const DOWNLOAD_URL_REVOKE_DELAY_MS = 5_000;
const SECTION_NUMBERS: readonly string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
const SECTION_TITLES: Readonly<Record<string, string>> = {
  '1': 'ATA CLASSIFICATION',
  '2': 'FAULT ISOLATION (FIM)',
  '3': 'CORRECTIVE ACTION (AMM)',
  '4': 'PARTS AND MATERIALS',
  '5': 'TOOLING AND EQUIPMENT',
  '6': 'MEL / CDL APPLICABILITY',
  '7': 'OPERATIONAL IMPACT ASSESSMENT',
  '8': 'SERVICE BULLETINS AND AIRWORTHINESS DIRECTIVES',
  '9': 'REFERENCES',
  '10': 'CRITICAL NOTES AND CAUTIONS',
};

type AnalysisStatus = 'idle' | 'running' | 'complete' | 'error';

interface RunAnalysisOptions {
  carrier: StandaloneSkyWrenchCarrier;
  defectDescription: string;
  generation: number;
  isCurrent: (generation: number) => boolean;
  onSection: (section: StandaloneSkyWrenchSection) => void;
}

async function runAnalysisSession(options: RunAnalysisOptions): Promise<number> {
  let failedSectionCount: number = 0;
  for (const sectionNumber of SECTION_NUMBERS) {
    if (!options.isCurrent(options.generation)) return failedSectionCount;
    try {
      const section: StandaloneSkyWrenchSection = await requestStandaloneSkyWrenchSection({
        carrier: options.carrier,
        defectDescription: options.defectDescription,
        section: sectionNumber,
      });
      if (options.isCurrent(options.generation)) options.onSection(section);
    } catch (error: unknown) {
      failedSectionCount += 1;
      if (!options.isCurrent(options.generation)) return failedSectionCount;
      options.onSection({
        section: sectionNumber,
        title: SECTION_TITLES[sectionNumber] || 'ANALYSIS ERROR',
        content: error instanceof Error ? error.message : 'Section analysis failed',
        error: true,
      });
    }
  }
  return failedSectionCount;
}
interface StandaloneAnalysisState {
  carrier: StandaloneSkyWrenchCarrier | '';
  defectDescription: string;
  status: AnalysisStatus;
  sections: StandaloneSkyWrenchSection[];
  error: string;
  setCarrier: (carrier: StandaloneSkyWrenchCarrier | '') => void;
  setDefectDescription: (defectDescription: string) => void;
  run: () => void;
  reset: () => void;
}

interface AnalysisOutputState {
  status: AnalysisStatus;
  sections: StandaloneSkyWrenchSection[];
  error: string;
  generationRef: React.MutableRefObject<number>;
  setStatus: React.Dispatch<React.SetStateAction<AnalysisStatus>>;
  setSections: React.Dispatch<React.SetStateAction<StandaloneSkyWrenchSection[]>>;
  setError: React.Dispatch<React.SetStateAction<string>>;
  clearOutput: () => void;
}

/**
 * Owns the analysis output — status, streamed sections, and error — together with
 * the generation counter that invalidates a superseded run.
 */
function useAnalysisOutput(): AnalysisOutputState {
  const [status, setStatus] = useState<AnalysisStatus>('idle');
  const [sections, setSections] = useState<StandaloneSkyWrenchSection[]>([]);
  const [error, setError] = useState<string>('');
  const generationRef = useRef<number>(0);
  const clearOutput = (): void => {
    generationRef.current += 1;
    setStatus('idle');
    setSections([]);
    setError('');
  };
  return { status, sections, error, generationRef, setStatus, setSections, setError, clearOutput };
}

interface StartAnalysisOptions {
  carrier: StandaloneSkyWrenchCarrier;
  defectDescription: string;
  generationRef: React.MutableRefObject<number>;
  setStatus: React.Dispatch<React.SetStateAction<AnalysisStatus>>;
  setSections: React.Dispatch<React.SetStateAction<StandaloneSkyWrenchSection[]>>;
  setError: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Starts a fresh analysis generation, appending each section as it arrives. A
 * newer generation supersedes this one, so late results are discarded.
 */
function startAnalysis(options: StartAnalysisOptions): void {
  const { carrier, defectDescription, generationRef, setStatus, setSections, setError } = options;
  const generation: number = generationRef.current + 1;
  generationRef.current = generation;
  setStatus('running');
  setSections([]);
  setError('');
  void runAnalysisSession({
    carrier,
    defectDescription,
    generation,
    isCurrent: (candidate: number): boolean => generationRef.current === candidate,
    onSection: (section: StandaloneSkyWrenchSection): void => {
      setSections((current: StandaloneSkyWrenchSection[]) => [...current, section]);
    },
  }).then((failedSectionCount: number): void => {
    if (generationRef.current !== generation) return;
    const allSectionsFailed: boolean = failedSectionCount === SECTION_NUMBERS.length;
    setStatus(allSectionsFailed ? 'error' : 'complete');
    if (allSectionsFailed) setError('SkyWrench could not complete the analysis.');
  });
}

function useStandaloneAnalysis(): StandaloneAnalysisState {
  const [carrier, setCarrierValue] = useState<StandaloneSkyWrenchCarrier | ''>('');
  const [defectDescription, setDefectValue] = useState<string>('');
  const output: AnalysisOutputState = useAnalysisOutput();
  const setCarrier = (value: StandaloneSkyWrenchCarrier | ''): void => {
    setCarrierValue(value);
    output.clearOutput();
  };
  const setDefectDescription = (value: string): void => {
    setDefectValue(value.slice(0, MAX_DEFECT_DESCRIPTION_LENGTH));
    output.clearOutput();
  };
  const reset = (): void => {
    output.clearOutput();
    setCarrierValue('');
    setDefectValue('');
  };
  const run = (): void => {
    if (!carrier || !defectDescription.trim() || output.status === 'running') return;
    startAnalysis({
      carrier,
      defectDescription: defectDescription.trim(),
      generationRef: output.generationRef,
      setStatus: output.setStatus,
      setSections: output.setSections,
      setError: output.setError,
    });
  };
  return {
    carrier,
    defectDescription,
    status: output.status,
    sections: output.sections,
    error: output.error,
    setCarrier,
    setDefectDescription,
    run,
    reset,
  };
}

interface ReferenceViewerState {
  openingUri: string | null;
  error: string;
  document: SkyWrenchReferenceAccess | null;
  open: (sourceUri: string) => Promise<void>;
  close: () => void;
}

function useReferenceViewer(): ReferenceViewerState {
  const [openingUri, setOpeningUri] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [document, setDocument] = useState<SkyWrenchReferenceAccess | null>(null);
  const open = async (sourceUri: string): Promise<void> => {
    setOpeningUri(sourceUri);
    setError('');
    try {
      setDocument(await resolveSkyWrenchReference(sourceUri));
    } catch (openError: unknown) {
      setError(openError instanceof Error ? openError.message : 'Reference could not be opened.');
    } finally {
      setOpeningUri(null);
    }
  };
  return { openingUri, error, document, open, close: (): void => setDocument(null) };
}
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildSectionHtml(section: StandaloneSkyWrenchSection): string {
  const heading: string = `${escapeHtml(section.section)}. ${escapeHtml(section.title)}`;
  if (section.section === '9')
    return `<h3>${heading}</h3><p>References are available in AETHER.</p>`;
  return `<h3>${heading}</h3><p>${escapeHtml(section.content)}</p>`;
}

function buildPrintHtml(
  carrier: StandaloneSkyWrenchCarrier,
  defectDescription: string,
  sections: readonly StandaloneSkyWrenchSection[],
): string {
  const sectionHtml: string = sections.map(buildSectionHtml).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>SkyWrench Report - ${carrier}</title>
<style>body{font-family:Arial,sans-serif;margin:40px;color:#1a1a1a}h2{color:#1e3a5f;border-bottom:2px solid #1e3a5f;padding-bottom:8px}h3{color:#2c5282;margin-top:24px}p{line-height:1.6;white-space:pre-wrap}@media print{body{margin:20px}}</style>
</head><body><h2>SkyWrench Analysis</h2><p><b>Carrier:</b> ${escapeHtml(carrier)}</p><p><b>Defect:</b> ${escapeHtml(defectDescription)}</p>${sectionHtml}</body></html>`;
}

function printAnalysis(
  carrier: StandaloneSkyWrenchCarrier,
  defectDescription: string,
  sections: readonly StandaloneSkyWrenchSection[],
): void {
  const url: string = URL.createObjectURL(
    new Blob([buildPrintHtml(carrier, defectDescription, sections)], { type: 'text/html' }),
  );
  const printWindow: Window | null = window.open(url, '_blank');
  if (printWindow) {
    printWindow.addEventListener('load', (): void => {
      printWindow.print();
      window.setTimeout((): void => URL.revokeObjectURL(url), PRINT_URL_REVOKE_DELAY_MS);
    });
    return;
  }
  const link: HTMLAnchorElement = document.createElement('a');
  link.href = url;
  link.download = `SkyWrench_${carrier}_${new Date().toISOString().slice(0, 10)}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout((): void => URL.revokeObjectURL(url), DOWNLOAD_URL_REVOKE_DELAY_MS);
}

interface StandaloneFormProps {
  analysis: StandaloneAnalysisState;
}

const StandaloneForm: React.FC<StandaloneFormProps> = ({ analysis }) => {
  const handleCarrierChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const value: string = event.target.value;
    analysis.setCarrier(isStandaloneSkyWrenchCarrier(value) ? value : '');
  };
  const isRunning: boolean = analysis.status === 'running';
  return (
    <div className="standalone-skywrench-form">
      <label className="standalone-skywrench-carrier">
        <span>Carrier Selection</span>
        <select value={analysis.carrier} onChange={handleCarrierChange} disabled={isRunning}>
          <option value="">Select Carrier</option>
          {STANDALONE_SKYWRENCH_CARRIERS.map((carrier: StandaloneSkyWrenchCarrier) => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </select>
      </label>
      <label className="standalone-skywrench-defect">
        <span>Defect</span>
        <textarea
          value={analysis.defectDescription}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
            analysis.setDefectDescription(event.target.value)
          }
          disabled={!analysis.carrier || isRunning}
          maxLength={MAX_DEFECT_DESCRIPTION_LENGTH}
          rows={4}
          placeholder="Enter the discrepancy to analyze"
        />
      </label>
      <button
        className="modal-btn modal-btn-save standalone-skywrench-run"
        type="button"
        onClick={analysis.run}
        disabled={!analysis.carrier || !analysis.defectDescription.trim() || isRunning}
      >
        {isRunning ? 'Running Analysis…' : 'Run Analysis'}
      </button>
    </div>
  );
};
interface StandaloneResultsProps {
  analysis: StandaloneAnalysisState;
  references: ReferenceViewerState;
}

const StandaloneResults: React.FC<StandaloneResultsProps> = ({ analysis, references }) => (
  <div className="standalone-skywrench-results" aria-live="polite">
    {analysis.status === 'running' && (
      <div className="standalone-skywrench-progress" role="status">
        <span className="refresh-spinner" aria-hidden="true" />
        Processing section {Math.min(analysis.sections.length + 1, SECTION_NUMBERS.length)} of{' '}
        {SECTION_NUMBERS.length}
      </div>
    )}
    {analysis.error && (
      <p className="standalone-skywrench-error" role="alert">
        {analysis.error}
      </p>
    )}
    {analysis.sections.map((section: StandaloneSkyWrenchSection) => (
      <div
        className={`skywrench-section${section.error ? ' skywrench-error' : ''}`}
        key={section.section}
      >
        <div className="skywrench-section-title">
          {section.section}. {section.title}
        </div>
        {section.section === '9' ? (
          <SkyWrenchReferenceCards
            sourceUris={section.sourceUris || []}
            openingUri={references.openingUri}
            error={references.error}
            onOpen={(sourceUri: string): void => {
              void references.open(sourceUri);
            }}
          />
        ) : (
          <p className="skywrench-text">{section.content}</p>
        )}
      </div>
    ))}
  </div>
);

interface StandaloneHeaderProps {
  canDownload: boolean;
  onDownload: () => void;
  onClose: () => void;
}

const StandaloneHeader: React.FC<StandaloneHeaderProps> = ({
  canDownload,
  onDownload,
  onClose,
}) => (
  <div className="modal-header skywrench-modal-header">
    <h2>
      <img className="skywrench-icon-logo" src={skyWrenchLogo} alt="" aria-hidden="true" />
      <span className="skywrench-modal-title-text">SkyWrench Analysis</span>
    </h2>
    <div className="skywrench-header-actions">
      {canDownload && (
        <button
          className="skywrench-action-btn"
          type="button"
          onClick={onDownload}
          aria-label="Download analysis"
          title="Download analysis as PDF"
        >
          ⬇
        </button>
      )}
      <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
        &times;
      </button>
    </div>
  </div>
);

interface StandaloneModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const StandaloneModal: React.FC<StandaloneModalProps> = ({ isOpen, onClose }) => {
  const analysis: StandaloneAnalysisState = useStandaloneAnalysis();
  const references: ReferenceViewerState = useReferenceViewer();
  if (!isOpen) return null;
  const close = (): void => {
    analysis.reset();
    references.close();
    onClose();
  };
  const download = (): void => {
    if (!analysis.carrier) return;
    printAnalysis(analysis.carrier, analysis.defectDescription, analysis.sections);
  };
  return (
    <div
      className="modal-overlay active"
      role="dialog"
      aria-modal="true"
      aria-label="Standalone SkyWrench Analysis"
    >
      <div className="modal skywrench-modal standalone-skywrench-modal">
        <StandaloneHeader
          canDownload={analysis.status === 'complete'}
          onDownload={download}
          onClose={close}
        />
        <div className="modal-body-scroll skywrench-modal-body">
          <StandaloneForm analysis={analysis} />
          <StandaloneResults analysis={analysis} references={references} />
        </div>
      </div>
      <SkyWrenchReferenceViewer document={references.document} onClose={references.close} />
    </div>
  );
};

/** Filter-bar launcher that owns the ephemeral standalone analysis popup state. */
const StandaloneSkyWrenchLauncher: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  return (
    <>
      <button
        className="filter-skywrench-launcher"
        type="button"
        onClick={(): void => setIsOpen(true)}
        aria-label="Open standalone SkyWrench analysis"
        title="Open SkyWrench analysis"
      >
        <img src={skyWrenchLogo} alt="" aria-hidden="true" />
      </button>
      <StandaloneModal isOpen={isOpen} onClose={(): void => setIsOpen(false)} />
    </>
  );
};

export default StandaloneSkyWrenchLauncher;
